import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Progress,
  Text,
  VStack,
  HStack,
  Badge,
  Skeleton,
  Alert,
  AlertIcon,
  Button,
  Icon,
  Flex,
  Divider,
} from '@chakra-ui/react';

import { FaClock, FaSync } from 'react-icons/fa';

import { getMarketplaceRateLimitApi } from 'features/marketplaces/marketplaces.api';
import { getStoresListApi } from 'features/stores/stores.api';

const RateLimitCard = () => {
  const dispatch = useDispatch();
  const [selectedStore, setSelectedStore] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const { rateLimit, getMarketplaceRateLimitStatus, error: rateLimitError } =
    useSelector((state) => state.marketplaces);
  const { list: stores, getStoresListStatus } = useSelector((state) => state.stores);

  // Filter eBay stores only
  const ebayStores =
    stores?.filter((store) => store.marketplace?.name?.toLowerCase() === 'ebay') || [];

  useEffect(() => {
    dispatch(getStoresListApi());
  }, [dispatch]);

  useEffect(() => {
    if (selectedStore) {
      dispatch(
        getMarketplaceRateLimitApi({
          marketplace: 'ebay',
          storeId: selectedStore.id,
        }),
      );
    }
  }, [dispatch, selectedStore]);

  useEffect(() => {
    if (autoRefresh && selectedStore) {
      const interval = setInterval(() => {
        dispatch(
          getMarketplaceRateLimitApi({
            marketplace: 'ebay',
            storeId: selectedStore.id,
          }),
        );
      }, 60000); // Refresh every minute

      return () => clearInterval(interval);
    }
  }, [autoRefresh, selectedStore, dispatch]);

  const handleRefresh = () => {
    if (selectedStore) {
      dispatch(
        getMarketplaceRateLimitApi({
          marketplace: 'ebay',
          storeId: selectedStore.id,
        }),
      );
    }
  };

  const getUsagePercentage = (used, limit) => {
    return Math.round((used / limit) * 100);
  };

  const getStatusColor = (percentage) => {
    if (percentage >= 90) return 'red';
    if (percentage >= 75) return 'orange';
    return 'green';
  };

  const getStatusBadge = (percentage) => {
    if (percentage >= 90) return { label: 'Critical', colorScheme: 'red' };
    if (percentage >= 75) return { label: 'High', colorScheme: 'orange' };
    return { label: 'Good', colorScheme: 'green' };
  };

  if (getStoresListStatus === 'pending') {
    return (
      <Card>
        <CardBody>
          <Skeleton height="200px" />
        </CardBody>
      </Card>
    );
  }

  if (ebayStores.length === 0) {
    return (
      <Card>
        <CardHeader>
          <Heading size="md">eBay Rate Limits</Heading>
        </CardHeader>
        <CardBody>
          <Alert status="info">
            <AlertIcon />
            <Text>No eBay stores found. Add an eBay store to view rate limits.</Text>
          </Alert>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <Flex justify="space-between" align="center">
          <Heading size="md">eBay Rate Limits</Heading>
          <HStack spacing={2}>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setAutoRefresh(!autoRefresh)}
              colorScheme={autoRefresh ? 'blue' : 'gray'}
              leftIcon={<FaSync />}
            >
              Auto Refresh
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleRefresh}
              isLoading={getMarketplaceRateLimitStatus === 'pending'}
              leftIcon={<FaSync />}
            >
              Refresh
            </Button>
          </HStack>
        </Flex>
      </CardHeader>
      <CardBody>
        <VStack spacing={4} align="stretch">
          {/* Store Selection */}
          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              Select eBay Store:
            </Text>
            <HStack spacing={2} flexWrap="wrap">
              {ebayStores.map((store) => (
                <Button
                  key={store.id}
                  size="sm"
                  variant={selectedStore?.id === store.id ? 'solid' : 'outline'}
                  colorScheme={selectedStore?.id === store.id ? 'blue' : 'gray'}
                  onClick={() => setSelectedStore(store)}
                >
                  {store.name}
                </Button>
              ))}
            </HStack>
          </Box>

          {selectedStore && (
            <>
              <Divider />

              {getMarketplaceRateLimitStatus === 'pending' ? (
                <VStack spacing={4}>
                  <Skeleton height="100px" width="100%" />
                  <Skeleton height="100px" width="100%" />
                </VStack>
              ) : getMarketplaceRateLimitStatus === 'rejected' ? (
                <Alert status="error">
                  <AlertIcon />
                  <VStack align="stretch" spacing={1}>
                    <Text fontWeight="medium">Failed to load rate limit data</Text>
                    {rateLimitError && (
                      <Text fontSize="sm" opacity={0.9}>
                        {rateLimitError}
                      </Text>
                    )}
                  </VStack>
                </Alert>
              ) : rateLimit?.tradingApiRateLimit?.limit != null &&
                rateLimit?.browseApiRateLimit?.limit != null ? (
                <VStack spacing={4} align="stretch">
                  {/* Reset Time */}
                  <Box textAlign="center" p={3} bg="gray.50" borderRadius="md">
                    <HStack justify="center" spacing={2}>
                      <Icon as={FaClock} color="blue.500" />
                      <Text fontSize="sm" fontWeight="medium">
                        Resets: {rateLimit.resetTime}
                      </Text>
                    </HStack>
                  </Box>

                  {/* Trading API Rate Limit */}
                  <Box>
                    <HStack justify="space-between" mb={2}>
                      <Text fontWeight="medium">Trading API</Text>
                      <Badge
                        {...getStatusBadge(
                          getUsagePercentage(
                            rateLimit.tradingApiRateLimit.limit -
                              rateLimit.tradingApiRateLimit.remaining,
                            rateLimit.tradingApiRateLimit.limit,
                          ),
                        )}
                      >
                        {
                          getStatusBadge(
                            getUsagePercentage(
                              rateLimit.tradingApiRateLimit.limit -
                                rateLimit.tradingApiRateLimit.remaining,
                              rateLimit.tradingApiRateLimit.limit,
                            ),
                          ).label
                        }
                      </Badge>
                    </HStack>
                    <VStack spacing={2} align="stretch">
                      <Progress
                        value={getUsagePercentage(
                          rateLimit.tradingApiRateLimit.limit -
                            rateLimit.tradingApiRateLimit.remaining,
                          rateLimit.tradingApiRateLimit.limit,
                        )}
                        colorScheme={getStatusColor(
                          getUsagePercentage(
                            rateLimit.tradingApiRateLimit.limit -
                              rateLimit.tradingApiRateLimit.remaining,
                            rateLimit.tradingApiRateLimit.limit,
                          ),
                        )}
                        size="lg"
                        borderRadius="md"
                      />
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">
                          {rateLimit.tradingApiRateLimit.remaining} remaining
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          {rateLimit.tradingApiRateLimit.limit} total
                        </Text>
                      </HStack>
                    </VStack>
                  </Box>

                  {/* Browse API Rate Limit */}
                  <Box>
                    <HStack justify="space-between" mb={2}>
                      <Text fontWeight="medium">Browse API</Text>
                      <Badge
                        {...getStatusBadge(
                          getUsagePercentage(
                            rateLimit.browseApiRateLimit.limit -
                              rateLimit.browseApiRateLimit.remaining,
                            rateLimit.browseApiRateLimit.limit,
                          ),
                        )}
                      >
                        {
                          getStatusBadge(
                            getUsagePercentage(
                              rateLimit.browseApiRateLimit.limit -
                                rateLimit.browseApiRateLimit.remaining,
                              rateLimit.browseApiRateLimit.limit,
                            ),
                          ).label
                        }
                      </Badge>
                    </HStack>
                    <VStack spacing={2} align="stretch">
                      <Progress
                        value={getUsagePercentage(
                          rateLimit.browseApiRateLimit.limit -
                            rateLimit.browseApiRateLimit.remaining,
                          rateLimit.browseApiRateLimit.limit,
                        )}
                        colorScheme={getStatusColor(
                          getUsagePercentage(
                            rateLimit.browseApiRateLimit.limit -
                              rateLimit.browseApiRateLimit.remaining,
                            rateLimit.browseApiRateLimit.limit,
                          ),
                        )}
                        size="lg"
                        borderRadius="md"
                      />
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">
                          {rateLimit.browseApiRateLimit.remaining} remaining
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          {rateLimit.browseApiRateLimit.limit} total
                        </Text>
                      </HStack>
                    </VStack>
                  </Box>

                  {/* Feed API Rate Limit */}
                  {rateLimit.feedApiRateLimit && rateLimit.feedApiRateLimit.limit > 0 && (
                    <Box>
                      <HStack justify="space-between" mb={2}>
                        <Text fontWeight="medium">Feed API (Sell v1)</Text>
                        <Badge
                          {...getStatusBadge(
                            getUsagePercentage(
                              rateLimit.feedApiRateLimit.limit -
                                rateLimit.feedApiRateLimit.remaining,
                              rateLimit.feedApiRateLimit.limit,
                            ),
                          )}
                        >
                          {
                            getStatusBadge(
                              getUsagePercentage(
                                rateLimit.feedApiRateLimit.limit -
                                  rateLimit.feedApiRateLimit.remaining,
                                rateLimit.feedApiRateLimit.limit,
                              ),
                            ).label
                          }
                        </Badge>
                      </HStack>
                      <VStack spacing={2} align="stretch">
                        <Progress
                          value={getUsagePercentage(
                            rateLimit.feedApiRateLimit.limit -
                              rateLimit.feedApiRateLimit.remaining,
                            rateLimit.feedApiRateLimit.limit,
                          )}
                          colorScheme={getStatusColor(
                            getUsagePercentage(
                              rateLimit.feedApiRateLimit.limit -
                                rateLimit.feedApiRateLimit.remaining,
                              rateLimit.feedApiRateLimit.limit,
                            ),
                          )}
                          size="lg"
                          borderRadius="md"
                        />
                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.600">
                            {rateLimit.feedApiRateLimit.remaining} remaining
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            {rateLimit.feedApiRateLimit.limit} total
                          </Text>
                        </HStack>
                      </VStack>
                    </Box>
                  )}

                  {/* Analytics API Rate Limit */}
                  {rateLimit.analyticsApiRateLimit &&
                    rateLimit.analyticsApiRateLimit.limit > 0 && (
                      <Box>
                        <HStack justify="space-between" mb={2}>
                          <Text fontWeight="medium">Analytics API (Developer v1)</Text>
                          <Badge
                            {...getStatusBadge(
                              getUsagePercentage(
                                rateLimit.analyticsApiRateLimit.limit -
                                  rateLimit.analyticsApiRateLimit.remaining,
                                rateLimit.analyticsApiRateLimit.limit,
                              ),
                            )}
                          >
                            {
                              getStatusBadge(
                                getUsagePercentage(
                                  rateLimit.analyticsApiRateLimit.limit -
                                    rateLimit.analyticsApiRateLimit.remaining,
                                  rateLimit.analyticsApiRateLimit.limit,
                                ),
                              ).label
                            }
                          </Badge>
                        </HStack>
                        <VStack spacing={2} align="stretch">
                          <Progress
                            value={getUsagePercentage(
                              rateLimit.analyticsApiRateLimit.limit -
                                rateLimit.analyticsApiRateLimit.remaining,
                              rateLimit.analyticsApiRateLimit.limit,
                            )}
                            colorScheme={getStatusColor(
                              getUsagePercentage(
                                rateLimit.analyticsApiRateLimit.limit -
                                  rateLimit.analyticsApiRateLimit.remaining,
                                rateLimit.analyticsApiRateLimit.limit,
                              ),
                            )}
                            size="lg"
                            borderRadius="md"
                          />
                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">
                              {rateLimit.analyticsApiRateLimit.remaining} remaining
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              {rateLimit.analyticsApiRateLimit.limit} total
                            </Text>
                          </HStack>
                        </VStack>
                      </Box>
                    )}

                  {/* Warning for high usage */}
                  {(getUsagePercentage(
                    rateLimit.tradingApiRateLimit.limit -
                      rateLimit.tradingApiRateLimit.remaining,
                    rateLimit.tradingApiRateLimit.limit,
                  ) >= 75 ||
                    getUsagePercentage(
                      rateLimit.browseApiRateLimit.limit -
                        rateLimit.browseApiRateLimit.remaining,
                      rateLimit.browseApiRateLimit.limit,
                    ) >= 75 ||
                    (rateLimit.feedApiRateLimit &&
                      rateLimit.feedApiRateLimit.limit > 0 &&
                      getUsagePercentage(
                        rateLimit.feedApiRateLimit.limit -
                          rateLimit.feedApiRateLimit.remaining,
                        rateLimit.feedApiRateLimit.limit,
                      ) >= 75) ||
                    (rateLimit.analyticsApiRateLimit &&
                      rateLimit.analyticsApiRateLimit.limit > 0 &&
                      getUsagePercentage(
                        rateLimit.analyticsApiRateLimit.limit -
                          rateLimit.analyticsApiRateLimit.remaining,
                        rateLimit.analyticsApiRateLimit.limit,
                      ) >= 75)) && (
                    <Alert status="warning">
                      <AlertIcon />
                      <Text fontSize="sm">
                        Rate limit usage is high. Consider reducing API call frequency.
                      </Text>
                    </Alert>
                  )}
                </VStack>
              ) : rateLimit ? (
                <Alert status="warning">
                  <AlertIcon />
                  <Text>Rate limit data is unavailable for this store.</Text>
                </Alert>
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  <Text>Select a store to view rate limits</Text>
                </Alert>
              )}
            </>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default RateLimitCard;
